/**
 * Measured ("observed") and weather-aware forecast water levels. Unlike the yearly astronomical
 * predictions these have no year boundary: a GridSeries simply starts at its first point.
 */
/** Place points on a fixed grid; points that are not on the grid are dropped */
export function pointsToGrid(points, intervalMinutes = 10) {
    const step = intervalMinutes * 60_000;
    const onGrid = points.filter(p => p.time % step === 0).sort((a, b) => a.time - b.time);
    if (onGrid.length === 0)
        return undefined;
    const start = onGrid[0].time;
    const n = (onGrid[onGrid.length - 1].time - start) / step + 1;
    const values = new Array(n).fill(null);
    for (const p of onGrid)
        values[(p.time - start) / step] = p.level;
    return { start, intervalMinutes, values };
}
/** The non-null points of a grid series, optionally limited to [from, to] */
export function gridToPoints(s, from = -Infinity, to = Infinity) {
    const step = s.intervalMinutes * 60_000;
    const out = [];
    const first = Math.max(0, Math.ceil((from - s.start) / step));
    const last = Math.min(s.values.length - 1, Math.floor((to - s.start) / step));
    for (let i = first; i <= last; i++) {
        const v = s.values[i];
        if (v !== null && v !== undefined)
            out.push({ time: s.start + i * step, level: v });
    }
    return out;
}
/** Drop everything before `from`; undefined when nothing is left */
export function trimGrid(s, from) {
    return pointsToGrid(gridToPoints(s, from), s.intervalMinutes);
}
/**
 * Lay two series over one another on a single grid, the first winning where they overlap.
 *
 * That order matters and is the point: a measurement and a forecast for the same ten
 * minutes are not equally true. The forecast for this afternoon was made this morning;
 * the measurement is what the gauge saw. Where the two cover the same minute, the gauge
 * wins, and the forecast only carries the curve past where the measurements stop.
 */
export function mergeGrids(first, second) {
    if (!first)
        return second;
    if (!second)
        return first;
    const byTime = new Map();
    for (const p of gridToPoints(second))
        byTime.set(p.time, p.level);
    for (const p of gridToPoints(first))
        byTime.set(p.time, p.level);
    return pointsToGrid([...byTime].sort((a, b) => a[0] - b[0]).map(([time, level]) => ({ time, level })), first.intervalMinutes);
}
/** "2026-09-10 02:00:00+02:00" (BSH) or ISO 8601 → epoch ms */
function parseTimestamp(text) {
    const t = Date.parse(text.replace(' ', 'T'));
    if (Number.isNaN(t))
        throw new Error(`Bad timestamp ${text}`);
    return t;
}
function cm(v) {
    if (v === undefined || v === null || v === '')
        return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? Math.round(n) : undefined;
}
/**
 * BSH water-level forecast API (gdi.bsh.de WaterLevelForecast, CC BY 4.0): a GeoJSON feature
 * per gauge whose `curve` lists 10-minute values above gauge zero: `tidal_prediction` always,
 * `measurement` for the past hours and `automated_curve_forecast` for the coming days.
 */
export function parseBshForecast(json, gaugeZeroFallbackCm) {
    const feature = json;
    const p = feature?.properties;
    if (!p || typeof p !== 'object')
        throw new Error('Not a BSH forecast feature');
    // Some gauges come without their gauge zero; the yearly prediction file (D01 record) then supplies it
    const gaugeZero = cm(p['gaugezero_relative_to_nhn']) ?? gaugeZeroFallbackCm;
    if (gaugeZero === undefined)
        throw new Error('No gauge zero in BSH forecast');
    const curve = Array.isArray(p['curve']) ? p['curve'] : [];
    const observed = [];
    const forecast = [];
    for (const e of curve) {
        if (typeof e['timestamp'] !== 'string')
            continue;
        const time = parseTimestamp(e['timestamp']);
        const m = cm(e['measurement']);
        const f = cm(e['automated_curve_forecast']);
        if (m !== undefined)
            observed.push({ time, level: m + gaugeZero });
        else if (f !== undefined)
            forecast.push({ time, level: f + gaugeZero });
    }
    observed.sort((a, b) => a.time - b.time);
    forecast.sort((a, b) => a.time - b.time);
    const events = [];
    const hwnw = Array.isArray(p['high_water_low_water']) ? p['high_water_low_water'] : [];
    for (const e of hwnw) {
        if (typeof e['event_timestamp'] !== 'string')
            continue;
        const ev = { time: parseTimestamp(e['event_timestamp']), type: e['event'] === 'HW' ? 'HW' : 'LW' };
        const f = cm(e['forecast_value']);
        const a = cm(e['tidal_prediction_value']);
        if (f !== undefined)
            ev.forecast = f + gaugeZero;
        if (a !== undefined)
            ev.astronomical = a + gaugeZero;
        events.push(ev);
    }
    events.sort((a, b) => a.time - b.time);
    const result = { gaugeLabel: String(p['gauge_label'] ?? ''), gaugeZeroNhnCm: gaugeZero, observed, forecast, events };
    if (p['operator_gauge_id'] !== undefined && p['operator_gauge_id'] !== null && p['operator_gauge_id'] !== '')
        result.operatorGaugeId = String(p['operator_gauge_id']);
    const issued = p['automated_curveforecast_timestamp'] ?? p['forecast_timestamp'];
    if (typeof issued === 'string')
        result.forecastIssuedAt = new Date(parseTimestamp(issued)).toISOString();
    return result;
}
/**
 * A 10-minute curve from forecast high and low waters, for gauges without a curve forecast:
 * a half cosine between each pair of consecutive forecast extremes, as the Delphi app did.
 */
export function curveFromEvents(events, intervalMinutes = 10) {
    const step = intervalMinutes * 60_000;
    const out = [];
    const known = events.filter(e => e.forecast !== undefined);
    for (let i = 0; i + 1 < known.length; i++) {
        const a = known[i];
        const b = known[i + 1];
        if (b.time <= a.time)
            continue;
        const mean = (a.forecast + b.forecast) / 2;
        const amplitude = (a.forecast - b.forecast) / 2;
        for (let t = Math.ceil(a.time / step) * step; t < b.time; t += step) {
            const f = (t - a.time) / (b.time - a.time);
            out.push({ time: t, level: Math.round(mean + amplitude * Math.cos(Math.PI * f)) });
        }
    }
    return out;
}
/**
 * Pegelonline (WSV) measurements: `[{ timestamp, value }]` with the value in cm above gauge
 * zero, usually one per minute. Only the values on the 10-minute grid are kept.
 */
export function parsePegelonline(json, gaugeZeroNhnCm, intervalMinutes = 10) {
    if (!Array.isArray(json))
        throw new Error('Not a Pegelonline measurement list');
    const step = intervalMinutes * 60_000;
    const out = [];
    for (const e of json) {
        if (typeof e.timestamp !== 'string')
            continue;
        const time = parseTimestamp(e.timestamp);
        const v = cm(e.value);
        if (v === undefined || time % step !== 0)
            continue;
        out.push({ time, level: v + gaugeZeroNhnCm });
    }
    return out.sort((a, b) => a.time - b.time);
}
//# sourceMappingURL=actuals.js.map